/**
 * Settings Page
 *
 * Profile configuration and publishing settings.
 */

import { useEffect, useState } from 'preact/hooks';
import { useAuth, UserButton } from '@clerk/clerk-react';
import { api } from '../api/client';
import { profileApi } from '../api/profile';
import { useTheme, type ThemePreference } from '../hooks/useTheme';

interface Profile {
	id: string;
	slug: string;
	displayName: string;
	isPublic: boolean;
	chatEnabled: boolean;
	resumeUrl: string | null;
	linkedinUrl: string | null;
	portfolioUrl: string | null;
	allowedOrigins: string[];
	createdAt: number;
	updatedAt: number;
	publishedAt: number | null;
}

interface Snapshot {
	id: string;
	version: number;
	claimCount: number;
	generatedAt: number;
}

export function Settings() {
	const { getToken } = useAuth();
	const [profile, setProfile] = useState<Profile | null>(null);
	const [, setSnapshot] = useState<Snapshot | null>(null);
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [publishing, setPublishing] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [success, setSuccess] = useState<string | null>(null);

	// User tier + verification state
	const [userTier, setUserTier] = useState<'free' | 'pro'>('free');
	const [emailVerified, setEmailVerified] = useState(true);
	const [upgrading, setUpgrading] = useState(false);
	const [exporting, setExporting] = useState(false);

	// Form state
	const [slug, setSlug] = useState('');
	const [displayName, setDisplayName] = useState('');
	const [resumeUrl, setResumeUrl] = useState('');
	const [linkedinUrl, setLinkedinUrl] = useState('');
	const [portfolioUrl, setPortfolioUrl] = useState('');
	const [chatEnabled, setChatEnabled] = useState(false);
	const [slugAvailable, setSlugAvailable] = useState<boolean | null>(null);

	// Communication preferences (canonical identity rendering metadata)
	const [preferredName, setPreferredName] = useState('');
	const [namePronunciation, setNamePronunciation] = useState('');
	const [pronouns, setPronouns] = useState('');
	const [communicationLoaded, setCommunicationLoaded] = useState(false);
	const [communicationSaving, setCommunicationSaving] = useState(false);
	const [hasAnySession, setHasAnySession] = useState(false);

	useEffect(() => {
		loadProfile();
	}, []);

	async function loadProfile() {
		try {
			setLoading(true);
			const token = await getToken();
			if (!token) return;

			// Load user info (tier), profile, and canonical identity in parallel.
			// Canonical load is allowed to fail quietly — the card stays disabled if
			// the user hasn't started a session yet.
			const [data, userInfo, canonicalResult] = await Promise.all([
				profileApi.getProfile(token),
				api.getMe(token),
				api.getCanonical(token).catch(() => null),
			]);
			setUserTier(userInfo.tier);
			setEmailVerified(userInfo.emailVerified);

			if (canonicalResult?.canonical?.identity) {
				const identity = canonicalResult.canonical.identity;
				setPreferredName(identity.name?.preferred || '');
				setNamePronunciation(identity.name?.pronunciation || '');
				setPronouns(identity.pronouns || '');
				setCommunicationLoaded(true);
				setHasAnySession((canonicalResult.stats?.sessionCount || 0) > 0);
			} else {
				setCommunicationLoaded(true);
				setHasAnySession((canonicalResult?.stats?.sessionCount || 0) > 0);
			}

			// Check for upgrade/cancel query params
			const params = new URLSearchParams(window.location.search);
			if (params.get('upgrade') === 'success') {
				setSuccess('Welcome! Your subscription is active.');
				window.history.replaceState({}, '', '/settings');
			} else if (params.get('upgrade') === 'cancelled') {
				window.history.replaceState({}, '', '/settings');
			}

			if (data.profile) {
				setProfile(data.profile);
				setSlug(data.profile.slug);
				setDisplayName(data.profile.displayName);
				setResumeUrl(data.profile.resumeUrl || '');
				setLinkedinUrl(data.profile.linkedinUrl || '');
				setPortfolioUrl(data.profile.portfolioUrl || '');
				setChatEnabled(data.profile.chatEnabled);
			}
			if (data.snapshot) {
				setSnapshot(data.snapshot);
			}
		} catch (err: unknown) {
			// Profile not found is OK - user just hasn't created one
			if (err instanceof Error && !err.message.includes('not found')) {
				setError(err.message);
			}
		} finally {
			setLoading(false);
		}
	}

	async function checkSlug(newSlug: string) {
		if (!newSlug || newSlug === profile?.slug) {
			setSlugAvailable(null);
			return;
		}

		try {
			const token = await getToken();
			if (!token) return;

			const result = await profileApi.checkSlug(token, newSlug);
			setSlugAvailable(result.available);
		} catch (err) {
			setSlugAvailable(null);
		}
	}

	async function handleSave(e: Event) {
		e.preventDefault();
		if (saving) return;

		try {
			setSaving(true);
			setError(null);
			setSuccess(null);

			const token = await getToken();
			if (!token) return;

			const result = await profileApi.createOrUpdateProfile(token, {
				slug,
				displayName,
				resumeUrl: resumeUrl || null,
				linkedinUrl: linkedinUrl || null,
				portfolioUrl: portfolioUrl || null,
				chatEnabled,
			});

			setProfile(result.profile);
			setSuccess('Record saved successfully');
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Failed to save profile');
		} finally {
			setSaving(false);
		}
	}

	async function handleSaveCommunication(e: Event) {
		e.preventDefault();
		if (communicationSaving) return;

		try {
			setCommunicationSaving(true);
			setError(null);
			setSuccess(null);

			const token = await getToken();
			if (!token) return;

			const result = await api.updateIdentitySettings(token, {
				preferred: preferredName.trim(),
				pronunciation: namePronunciation,
				pronouns: pronouns,
			});

			// Reflect the canonical values the server returned (in case the compiler
			// normalized anything, and so the inputs stay in sync with canonical state).
			if (result.identity) {
				setPreferredName(result.identity.name?.preferred || '');
				setNamePronunciation(result.identity.name?.pronunciation || '');
				setPronouns(result.identity.pronouns || '');
			}
			setSuccess('Communication preferences saved');
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Failed to save preferences');
		} finally {
			setCommunicationSaving(false);
		}
	}

	async function handleUnpublish() {
		if (publishing) return;

		try {
			setPublishing(true);
			setError(null);

			const token = await getToken();
			if (!token) return;

			await profileApi.unpublish(token);
			setSuccess('Unpublished');
			await loadProfile();
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Failed to unpublish profile');
		} finally {
			setPublishing(false);
		}
	}

	const [selectedPlan, setSelectedPlan] = useState<'monthly' | 'annual'>('monthly');

	async function handleUpgrade() {
		if (upgrading) return;
		try {
			setUpgrading(true);
			setError(null);
			const token = await getToken();
			if (!token) return;

			const { url } = await api.createCheckoutSession(token, selectedPlan);
			window.location.href = url;
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Failed to start checkout');
			setUpgrading(false);
		}
	}

	async function handleManageSubscription() {
		try {
			setError(null);
			const token = await getToken();
			if (!token) return;

			const { url } = await api.createBillingPortal(token);
			window.location.href = url;
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Failed to open billing portal');
		}
	}

	async function handleExportData() {
		if (exporting) return;
		try {
			setExporting(true);
			setError(null);
			const token = await getToken();
			if (!token) return;

			const blob = await api.exportAllData(token);
			const url = URL.createObjectURL(blob);
			const a = document.createElement('a');
			a.href = url;
			a.download = `pxtxt-export-${Date.now()}.json`;
			document.body.appendChild(a);
			a.click();
			document.body.removeChild(a);
			URL.revokeObjectURL(url);
			setSuccess('Data exported successfully');
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Failed to export data');
		} finally {
			setExporting(false);
		}
	}

	if (loading) {
		return (
			<div class="page settings-page">
				<Header />
				<main class="settings-main">
					<div class="container">
						<div class="loading-spinner">Loading...</div>
					</div>
				</main>
				<style>{settingsStyles}</style>
			</div>
		);
	}

	return (
		<div class="page settings-page">
			<Header />

			<main class="settings-main">
				<div class="container">
					<h1 class="settings-title">Settings</h1>

					{!emailVerified && (
						<div class="alert alert-warning">
							Please verify your email address. Check your inbox for a verification link.
						</div>
					)}

					{error && (
						<div class="alert alert-error">
							{error}
							<button onClick={() => setError(null)}>Dismiss</button>
						</div>
					)}

					{success && (
						<div class="alert alert-success">
							{success}
							<button onClick={() => setSuccess(null)}>Dismiss</button>
						</div>
					)}

					{/* Communication Preferences — how pxtxt refers to the professional.
					    Separate <form> so Save only writes the canonical identity claims,
					    not the publish-layer profile row. */}
					<form class="settings-form settings-form--communication" onSubmit={handleSaveCommunication}>
						<section class="settings-section">
							<h2 class="settings-section-title">How pxtxt refers to you</h2>
							<p class="settings-section-desc">
								Rendering preferences for how your representation agent, narrative, and reports refer to you. These are communication settings — not identity declarations — and are optional.
							</p>

							{!hasAnySession && communicationLoaded && (
								<p class="form-hint">Start an interview first — these preferences attach to your record once you have one.</p>
							)}

							<div class="form-group">
								<label for="preferredName">Preferred Name</label>
								<input
									type="text"
									id="preferredName"
									value={preferredName}
									onInput={(e) => setPreferredName((e.target as HTMLInputElement).value)}
									placeholder="What you go by (e.g., Kim)"
									disabled={!hasAnySession}
								/>
								<span class="form-hint">The name pxtxt uses when referring to you. Defaults to the first name on your record.</span>
							</div>

							<div class="form-group">
								<label for="namePronunciation">Name Pronunciation</label>
								<input
									type="text"
									id="namePronunciation"
									value={namePronunciation}
									onInput={(e) => setNamePronunciation((e.target as HTMLInputElement).value)}
									placeholder="Phonetic spelling, e.g., KEEM or eye-REE-nay"
									disabled={!hasAnySession}
								/>
								<span class="form-hint">Optional. Helps pxtxt represent your name correctly when it matters.</span>
							</div>

							<div class="form-group">
								<label for="pronouns">Pronouns</label>
								<input
									type="text"
									id="pronouns"
									value={pronouns}
									onInput={(e) => setPronouns((e.target as HTMLInputElement).value)}
									placeholder="e.g., he/him, she/her, they/them"
									disabled={!hasAnySession}
								/>
								<span class="form-hint">Optional. If left blank, pxtxt uses your name or neutral construction — it will never guess.</span>
							</div>

							<div class="settings-actions">
								<button
									type="submit"
									class="btn btn-primary"
									disabled={communicationSaving || !hasAnySession}
								>
									{communicationSaving ? 'Saving...' : 'Save Preferences'}
								</button>
							</div>
						</section>
					</form>

					<form class="settings-form" onSubmit={handleSave}>
						{/* Basic Info */}
						<section class="settings-section">
							<h2 class="settings-section-title">Basic Information</h2>

							<div class="form-group">
								<label for="displayName">Display Name</label>
								<input
									type="text"
									id="displayName"
									value={displayName}
									onInput={(e) => setDisplayName((e.target as HTMLInputElement).value)}
									placeholder="Your full name"
									required
								/>
							</div>

							<div class="form-group">
								<label for="slug">Public URL</label>
								<div class="input-with-prefix">
									<span class="input-prefix">pxtxt.ai/u/</span>
									<input
										type="text"
										id="slug"
										value={slug}
										onInput={(e) => {
											const newSlug = (e.target as HTMLInputElement).value.toLowerCase().replace(/[^a-z0-9-]/g, '');
											setSlug(newSlug);
											checkSlug(newSlug);
										}}
										placeholder="your-slug"
										pattern="[a-z0-9-]+"
										required
										disabled={userTier !== 'pro'}
									/>
								</div>
								{userTier !== 'pro' ? (
									<span class="form-hint">Custom URLs require a paid subscription</span>
								) : (
									<>
										{slugAvailable === true && <span class="form-hint form-hint--success">Available</span>}
										{slugAvailable === false && <span class="form-hint form-hint--error">Already taken</span>}
									</>
								)}
							</div>
						</section>

						{/* Links */}
						<section class="settings-section">
							<h2 class="settings-section-title">Links</h2>

							<div class="form-group">
								<label for="resumeUrl">Resume URL</label>
								<input
									type="url"
									id="resumeUrl"
									value={resumeUrl}
									onInput={(e) => setResumeUrl((e.target as HTMLInputElement).value)}
									placeholder="https://..."
								/>
								<span class="form-hint">Link to your hosted resume (PDF, personal site, etc.)</span>
							</div>

							<div class="form-group">
								<label for="linkedinUrl">LinkedIn URL</label>
								<input
									type="url"
									id="linkedinUrl"
									value={linkedinUrl}
									onInput={(e) => setLinkedinUrl((e.target as HTMLInputElement).value)}
									placeholder="https://linkedin.com/in/..."
								/>
							</div>

							<div class="form-group">
								<label for="portfolioUrl">Portfolio URL</label>
								<input
									type="url"
									id="portfolioUrl"
									value={portfolioUrl}
									onInput={(e) => setPortfolioUrl((e.target as HTMLInputElement).value)}
									placeholder="https://..."
								/>
							</div>
						</section>

						{/* Chat Settings */}
						<section class="settings-section">
							<h2 class="settings-section-title">Chat Widget</h2>

							<div class="form-group form-group--checkbox">
								<label>
									<input
										type="checkbox"
										checked={chatEnabled}
										onChange={(e) => setChatEnabled((e.target as HTMLInputElement).checked)}
									/>
									<span>Enable chat widget on public profile</span>
								</label>
								<p class="form-hint">Allow visitors to chat with your representation agent</p>
							</div>
						</section>

						<div class="settings-actions">
							<button type="submit" class="btn btn-primary" disabled={saving}>
								{saving ? 'Saving...' : 'Save Changes'}
							</button>
						</div>
					</form>

					{/* Subscription Section */}
					<section class="settings-section settings-section--subscription">
						<h2 class="settings-section-title">Subscription</h2>
						{userTier === 'pro' ? (
							<div class="subscription-status">
								<div class="subscription-info">
									<span class="publish-status-badge">Subscribed</span>
									<p>Opportunity analysis, ongoing engagement reports, and custom profile URL.</p>
								</div>
								<button
									class="btn btn-secondary"
									onClick={handleManageSubscription}
								>
									Manage Subscription
								</button>
							</div>
						) : (
							<div class="subscription-upgrade">
								<div class="subscription-info">
									<span class="publish-status-badge publish-status-badge--draft">Free</span>
									<p>Your profile is live with AI-powered representation. Upgrade for unlimited sessions, tools, ongoing engagement reports, and custom profile URLs.</p>
								</div>
								<div class="plan-options">
									<label class={`plan-option ${selectedPlan === 'monthly' ? 'plan-option--selected' : ''}`}>
										<input
											type="radio"
											name="plan"
											value="monthly"
											checked={selectedPlan === 'monthly'}
											onChange={() => setSelectedPlan('monthly')}
										/>
										<span class="plan-option-price">$12/mo</span>
									</label>
									<label class={`plan-option ${selectedPlan === 'annual' ? 'plan-option--selected' : ''}`}>
										<input
											type="radio"
											name="plan"
											value="annual"
											checked={selectedPlan === 'annual'}
											onChange={() => setSelectedPlan('annual')}
										/>
										<span class="plan-option-price">$120/year</span>
										<span class="plan-option-save">Save $24</span>
									</label>
								</div>
								<button
									class="btn btn-primary"
									onClick={handleUpgrade}
									disabled={upgrading}
								>
									{upgrading ? 'Redirecting...' : 'Subscribe'}
								</button>
							</div>
						)}
					</section>

					{/* Data & Privacy Section */}
					<section class="settings-section settings-section--data">
						<h2 class="settings-section-title">Data & Privacy</h2>
						<div class="data-section">
							<div class="data-section-info">
								<p>Your data belongs to you. Export everything — profile settings, approved claims, pending claims, and session history — as a JSON file.</p>
							</div>
							<button
								class="btn btn-secondary"
								onClick={handleExportData}
								disabled={exporting}
							>
								{exporting ? 'Exporting...' : 'Export All Data'}
							</button>
						</div>
					</section>

					{/* Appearance Section */}
					<AppearanceSection />

					{/* Publishing Section */}
					<section class="settings-section settings-section--publish">
						<h2 class="settings-section-title">Publishing</h2>

						{profile?.isPublic ? (
							<div class="publish-status publish-status--live">
								<div class="publish-status-info">
									<span class="publish-status-badge">Live</span>
									<p>Your profile is public at <a href={`/b/${profile.slug}`} target="_blank">pxtxt.ai/b/{profile.slug}</a></p>
								</div>
								<div class="publish-actions">
									<button
										class="btn btn-secondary"
										onClick={handleUnpublish}
										disabled={publishing}
									>
										{publishing ? 'Unpublishing...' : 'Unpublish'}
									</button>
								</div>
							</div>
						) : (
							<div class="publish-status">
								<div class="publish-status-info">
									<span class="publish-status-badge publish-status-badge--draft">Draft</span>
									<p>Publish your Profile from the <a href="/dashboard">Dashboard</a>.</p>
								</div>
							</div>
						)}
					</section>
				</div>
			</main>

			<style>{settingsStyles}</style>
		</div>
	);
}

function AppearanceSection() {
	const { preference, setPreference } = useTheme();
	const options: { value: ThemePreference; label: string; desc: string }[] = [
		{ value: 'system', label: 'System', desc: 'Follow your OS setting' },
		{ value: 'light', label: 'Light', desc: '' },
		{ value: 'dark', label: 'Dark', desc: '' },
	];

	return (
		<section class="settings-section settings-section--appearance">
			<h2 class="settings-section-title">Appearance</h2>
			<div class="theme-options">
				{options.map(opt => (
					<button
						key={opt.value}
						class={`theme-option ${preference === opt.value ? 'theme-option--active' : ''}`}
						onClick={() => setPreference(opt.value)}
					>
						<span class="theme-option-label">{opt.label}</span>
						{opt.desc && <span class="theme-option-desc">{opt.desc}</span>}
					</button>
				))}
			</div>
		</section>
	);
}

function Header() {
	return (
		<header class="settings-header">
			<div class="container">
				<div class="header-left">
					<a href="/dashboard" class="back-link">
						<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
							<path d="M15 18l-6-6 6-6" />
						</svg>
						Dashboard
					</a>
					<span class="header-title">Settings</span>
				</div>
				<div class="header-right">
					<UserButton afterSignOutUrl="/" />
				</div>
			</div>
		</header>
	);
}

const settingsStyles = `
	.settings-page {
		min-height: 100vh;
		display: flex;
		flex-direction: column;
		background: var(--color-bg);
	}

	.settings-header {
		padding: 0.75rem 0;
		border-bottom: 1px solid var(--color-border);
		background: var(--color-bg-elevated);
	}

	.settings-header .container {
		display: flex;
		align-items: center;
		justify-content: space-between;
	}

	.header-left {
		display: flex;
		align-items: center;
		gap: 1rem;
	}

	.back-link {
		display: flex;
		align-items: center;
		gap: 0.375rem;
		color: var(--color-text-muted);
		font-family: var(--font-mono);
		font-size: 0.75rem;
		text-decoration: none;
		transition: color 150ms;
	}

	.back-link:hover {
		color: var(--color-text);
	}

	.header-title {
		font-family: var(--font-display);
		font-weight: 300;
		color: var(--color-text);
	}

	.header-right {
		display: flex;
		align-items: center;
		gap: 0.75rem;
	}

	.settings-main {
		flex: 1;
		padding: 2rem 0;
	}

	.container {
		max-width: 640px;
	}

	.settings-title {
		font-family: var(--font-display);
		font-size: 1.75rem;
		font-weight: 300;
		color: var(--color-text);
		margin: 0 0 2rem;
	}

	.settings-form {
		display: flex;
		flex-direction: column;
		gap: 2rem;
	}

	.settings-section {
		background: var(--color-bg-elevated);
		border: 1px solid var(--color-border);
		border-radius: 2px;
		padding: 1.5rem;
	}

	.settings-section--subscription {
		margin-top: 2rem;
	}

	.subscription-status {
		display: flex;
		align-items: center;
		justify-content: space-between;
		flex-wrap: wrap;
		gap: 1rem;
	}

	.subscription-upgrade {
		display: flex;
		flex-direction: column;
		gap: 1rem;
	}

	.subscription-info {
		flex: 1;
	}

	.subscription-info p {
		margin: 0.5rem 0 0;
		font-size: 0.9375rem;
		color: var(--color-text-muted);
	}

	.plan-options {
		display: flex;
		gap: 0.75rem;
	}

	.plan-option {
		display: flex;
		align-items: center;
		gap: 0.5rem;
		padding: 0.625rem 1rem;
		border: 1px solid var(--color-border);
		border-radius: 6px;
		cursor: pointer;
		transition: border-color 150ms ease;
	}

	.plan-option:hover {
		border-color: var(--color-text-muted);
	}

	.plan-option--selected {
		border-color: var(--color-text);
	}

	.plan-option input[type="radio"] {
		display: none;
	}

	.plan-option-price {
		font-family: var(--font-body);
		font-size: 0.9375rem;
		font-weight: 600;
		color: var(--color-text);
	}

	.plan-option-save {
		font-family: var(--font-mono);
		font-size: 11px;
		text-transform: uppercase;
		letter-spacing: 0.06em;
		color: var(--color-accent, #2a7);
	}

	.settings-section--data {
		margin-top: 2rem;
	}

	.data-section {
		display: flex;
		align-items: center;
		justify-content: space-between;
		flex-wrap: wrap;
		gap: 1rem;
	}

	.data-section-info {
		flex: 1;
	}

	.data-section-info p {
		margin: 0;
		font-size: 0.9375rem;
		color: var(--color-text-muted);
	}

	.settings-section--publish {
		margin-top: 2rem;
	}

	.publish-note {
		font-size: 0.8125rem;
		color: var(--color-text-muted);
		margin: -0.75rem 0 1rem;
	}

	.settings-section-title {
		font-family: var(--font-display);
		font-size: 1.125rem;
		font-weight: 300;
		color: var(--color-text);
		margin: 0 0 1.25rem;
	}

	.settings-section-desc {
		margin: -0.75rem 0 1.25rem;
		font-size: 0.875rem;
		color: var(--color-text-muted);
		line-height: 1.5;
	}

	.settings-form--communication {
		margin-bottom: 2rem;
	}

	.form-group {
		margin-bottom: 1rem;
	}

	.form-group:last-child {
		margin-bottom: 0;
	}

	.form-group label {
		display: block;
		font-family: var(--font-mono);
		font-size: 0.75rem;
		font-weight: 500;
		color: var(--color-text-muted);
		text-transform: uppercase;
		letter-spacing: 0.05em;
		margin-bottom: 0.5rem;
	}

	.form-group input[type="text"],
	.form-group input[type="url"] {
		width: 100%;
		padding: 0.625rem 0.75rem;
		background: var(--color-bg);
		border: 1px solid var(--color-border);
		border-radius: var(--radius-md);
		font-family: var(--font-body);
		font-size: 0.9375rem;
		color: var(--color-text);
		transition: border-color 150ms;
	}

	.form-group input:focus {
		outline: none;
		border-color: var(--color-primary);
	}

	.input-with-prefix {
		display: flex;
		align-items: center;
		background: var(--color-bg);
		border: 1px solid var(--color-border);
		border-radius: var(--radius-md);
		overflow: hidden;
	}

	.input-prefix {
		padding: 0.625rem 0 0.625rem 0.75rem;
		color: var(--color-text-muted);
		font-size: 0.9375rem;
		white-space: nowrap;
	}

	.input-with-prefix input,
	.input-with-prefix input[type="text"] {
		flex: 1;
		padding: 0.625rem 0.75rem 0.625rem 0;
		border: none !important;
		background: transparent !important;
		border-radius: 0;
	}

	.input-with-prefix:focus-within {
		border-color: var(--color-primary);
	}

	.form-hint {
		display: block;
		font-size: 0.8125rem;
		color: var(--color-text-muted);
		margin-top: 0.375rem;
	}

	.form-hint--success {
		color: var(--color-success);
	}

	.form-hint--error {
		color: var(--color-error);
	}

	.form-group--checkbox label {
		display: flex;
		align-items: center;
		gap: 0.5rem;
		cursor: pointer;
		text-transform: none;
		font-family: var(--font-body);
		font-size: 0.9375rem;
		color: var(--color-text);
	}

	.form-group--checkbox input[type="checkbox"] {
		width: 1.125rem;
		height: 1.125rem;
		accent-color: var(--color-primary);
	}

	.settings-actions {
		display: flex;
		justify-content: flex-end;
		gap: 0.75rem;
	}

	.publish-status {
		display: flex;
		align-items: center;
		justify-content: space-between;
		flex-wrap: wrap;
		gap: 1rem;
	}

	.publish-status-info {
		flex: 1;
	}

	.publish-status-info p {
		margin: 0.5rem 0 0;
		font-size: 0.9375rem;
		color: var(--color-text-muted);
	}

	.publish-status-info a {
		color: var(--color-primary);
		text-decoration: none;
	}

	.publish-status-info a:hover {
		text-decoration: underline;
	}

	.publish-meta {
		font-size: 0.8125rem;
		color: var(--color-text-subtle);
	}

	.publish-actions {
		display: flex;
		gap: 0.5rem;
		align-items: center;
	}

	.publish-status-badge {
		display: inline-block;
		padding: 0.25rem 0.5rem;
		background: var(--color-success);
		color: white;
		font-family: var(--font-mono);
		font-size: 0.6875rem;
		font-weight: 600;
		text-transform: uppercase;
		letter-spacing: 0.05em;
		border-radius: var(--radius-sm);
	}

	.publish-status-badge--draft {
		background: var(--color-text-muted);
	}

	.alert {
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: 0.75rem 1rem;
		margin-bottom: 1.5rem;
		border-radius: var(--radius-md);
		font-size: 0.9375rem;
	}

	.alert button {
		background: none;
		border: none;
		color: inherit;
		cursor: pointer;
		opacity: 0.7;
		font-size: 0.8125rem;
	}

	.alert button:hover {
		opacity: 1;
	}

	.alert-error {
		background: rgba(222, 46, 33, 0.1);
		border: 1px solid var(--color-error);
		color: var(--color-error);
	}

	.alert-warning {
		background: rgba(245, 166, 35, 0.1);
		border: 1px solid rgba(245, 166, 35, 0.4);
		color: var(--color-warning-text);
	}

	.alert-success {
		background: rgba(39, 174, 96, 0.1);
		border: 1px solid var(--color-success);
		color: var(--color-success);
	}

	.loading-spinner {
		text-align: center;
		padding: 3rem;
		color: var(--color-text-muted);
	}

	.pro-badge {
		display: inline-block;
		margin-left: 0.5rem;
		padding: 0.125rem 0.375rem;
		background: var(--color-primary);
		color: white;
		font-family: var(--font-mono);
		font-size: 0.625rem;
		font-weight: 600;
		text-transform: uppercase;
		letter-spacing: 0.05em;
		border-radius: var(--radius-sm);
		vertical-align: middle;
	}

	.gated-feature {
		opacity: 0.5;
		cursor: not-allowed;
	}

	.gated-feature input {
		cursor: not-allowed;
	}

	.settings-section--appearance {
		margin-top: 2rem;
	}

	.theme-options {
		display: flex;
		gap: 0.5rem;
	}

	.theme-option {
		flex: 1;
		padding: 0.75rem 1rem;
		background: var(--color-bg);
		border: 1px solid var(--color-border);
		border-radius: var(--radius-md);
		cursor: pointer;
		text-align: left;
		transition: border-color 150ms;
	}

	.theme-option:hover {
		border-color: var(--color-border-hover);
	}

	.theme-option--active {
		border-color: var(--color-primary);
		background: var(--color-primary-muted);
	}

	.theme-option-label {
		display: block;
		font-family: var(--font-body);
		font-size: 0.9375rem;
		font-weight: 500;
		color: var(--color-text);
	}

	.theme-option-desc {
		display: block;
		font-size: 0.75rem;
		color: var(--color-text-muted);
		margin-top: 0.125rem;
	}
`;
